import { PRIVACY_NOTICE_VERSION } from '../game/identity';
import { BrandHeader } from './BrandHeader';
import { RTNav } from './RTNav';

interface Props {
  onBack: () => void;
}

/**
 * The full privacy notice.
 *
 * Written to be read rather than to be survived. Everything here is a statement
 * of what the code actually does — if the code changes, this changes, and
 * PRIVACY_NOTICE_VERSION is bumped so existing consent records still say what
 * they were given against.
 */
export function PrivacyNotice({ onBack }: Props) {
  return (
    <div className="panel">
      <RTNav />
      <div className="panel__inner">
        <BrandHeader />
        <p className="case-eyebrow">Version {PRIVACY_NOTICE_VERSION}</p>
        <h1>Privacy notice</h1>
        <p className="lede">
          This game asks for your name and email. Here is exactly what happens to
          them.
        </p>

        <h2>Who holds your data</h2>
        <p>
          ReleaseTEAM is the controller of the information collected here. For
          anything on this page, contact{' '}
          <a href="mailto:info@releaseteam.com">info@releaseteam.com</a> or call
          866-887-0489.
        </p>

        <h2>What we collect, and why</h2>
        <ul className="rules">
          <li>
            <b>Your name and email address.</b> Your email is the identifier that
            ties your scores together across levels and devices. We send one
            six-digit code to confirm the address is yours — that check is what
            stops anyone else from playing, or requesting data, as you.
          </li>
          <li>
            <b>Your scores.</b> Which levels you played, what you scored, what
            you found and missed, and when.
          </li>
          <li>
            <b>A three-character handle, only if you choose one.</b> The public
            leaderboard shows handles, scores and how many cases somebody has
            closed — nothing else. It is entirely optional: without a handle you
            do not appear on it at all, and clearing your handle removes you
            immediately. Your name and email are never shown there.
          </li>
          <li>
            <b>Nothing else.</b> There is no analytics, no advertising, no
            tracking pixels, and no third-party scripts of any kind. The page
            loads nothing from any other company — even the font is served from
            this server rather than from Google, so your IP address is not
            shared.
          </li>
        </ul>

        <h2>Our lawful basis</h2>
        <p>
          We rely on <b>legitimate interests</b> to run the game and attribute
          your scores to you — you asked to play, and we cannot keep score
          without knowing who you are.
        </p>
        <p>
          If you ticked the box asking to hear from us about ReleaseTEAM
          services, that is separate and relies on your <b>consent</b>. It is
          optional, it is never required in order to play, and you can withdraw
          it at any time by replying to any message or emailing us. Withdrawing
          it is as easy as giving it.
        </p>

        <h2>Who else sees it</h2>
        <ul className="rules">
          <li>
            <b>Our own team, when you first sign up.</b> One notification with
            your name, your email and whether you opted into marketing goes to
            an internal ReleaseTEAM Slack channel and to an internal ReleaseTEAM
            mailbox, so we know somebody has started playing. It happens once,
            on your first sign-in — not every time you play — and it goes to our
            staff, not to anyone outside the company.
          </li>
          <li>
            <b>Our email provider,</b> to deliver the six-digit code. They
            process it on our behalf and for nothing else.
          </li>
          <li>
            <b>Nobody else.</b> No advertiser, no data broker, no analytics
            company. Opting out of marketing means we will not email you about
            our services — that choice is recorded and travels with your record
            wherever it is used internally.
          </li>
        </ul>

        <h2>What we do not do</h2>
        <ul className="rules">
          <li>
            <b>We do not sell or share your personal information</b> — including
            as those terms are defined under California law. There is no
            advertising and no data broker involved.
          </li>
          <li>
            <b>We do not publish who you are.</b> If you share a result, the
            shared page shows your score and rating only. The leaderboard shows
            the handle you chose. Your name and email are never on either.
          </li>
          <li>
            <b>We do not profile you or make automated decisions</b> that have
            any legal or similarly significant effect.
          </li>
        </ul>

        <h2>How long we keep it</h2>
        <p>
          Twelve months from your last play, after which records are deleted
          automatically. You do not need to ask.
        </p>

        <h2>Your rights</h2>
        <p>
          You can ask for a copy of your data, ask us to correct it, ask us to
          delete it, object to our processing, or ask for it in a portable form.
          If you are in the UK or EU you can also complain to your data
          protection authority; in the UK that is the ICO.
        </p>
        <p>
          Because your address is verified, two of these are self-service on the
          level select screen: <b>Download my data</b> gives you everything we
          hold about you as a JSON file, and <b>Delete my data</b> removes all of
          it immediately, including any result you have shared.
        </p>
        <p>
          For anything else — correction, objection, or a question about the
          above — email{' '}
          <a href="mailto:info@releaseteam.com">info@releaseteam.com</a>.
        </p>

        <h2>Storage on your device</h2>
        <p>
          We keep a sign-in token and your best scores in your browser’s local
          storage so you are not asked to verify your address every visit. The
          token expires after 30 days. That is functional storage for a game you
          asked to play — no cookies are set for advertising or analytics, which
          is why there is no cookie banner. “Not you?” signs you out on this
          device; “Delete my data” removes everything everywhere.
        </p>

        <h2>Where it is processed</h2>
        <p>
          On a ReleaseTEAM server hosted with Amazon Web Services in the United
          States.
        </p>

        <button type="button" className="btn btn--wide" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  );
}
